import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "../lib/utils";
import { Source } from "../lib/sources-utils";
import { markdownComponents } from "../lib/markdown-components";

interface MessageWithSourcesProps {
  content: string;
  sources: Source[];
  onSourceClick?: (source: Source) => void;
  className?: string;
}

export const MessageWithSources: React.FC<MessageWithSourcesProps> = ({
  content,
  sources,
  onSourceClick,
  className
}) => {
  // Repair streaming artifacts (broken headings, unintended line breaks) without being too aggressive.
  const repairStreamingMarkdown = (text: string): string => {
    // Fast exit if no newline (unlikely broken yet)
    if (!text.includes('\n')) return text;

    const lines = text.split(/\n/);
    const isHeading = (l: string) => /^#{1,6}\s+\S/.test(l.trim());
    const isListLike = (l: string) => /^(?:\s{0,3}[-*+] |\s{0,3}\d+\. )/.test(l);
    const isFenced = (l: string) => /^```/.test(l.trim());
    const isBlockStarter = (l: string) => isHeading(l) || isListLike(l) || /^>/.test(l.trim()) || isFenced(l);

    for (let i = 0; i < lines.length - 1; i++) {
      const line = lines[i];
      const next = lines[i + 1];
      if (!line || !next) continue;

      const trimmedLine = line.trimEnd();
      const trimmedNext = next.trim();

      // 1. Merge broken heading continuation: heading line short, doesn't end punctuation, next line looks like continuation (capitalized or alphanumeric) and not a block starter
      if (
        isHeading(trimmedLine) &&
        trimmedLine.length < 80 &&
        !/[.:!?]$/.test(trimmedLine) &&
        !isBlockStarter(trimmedNext) &&
        /^[A-Z0-9][A-Za-z0-9].{0,60}$/.test(trimmedNext) &&
        (trimmedLine.split(' ').length + trimmedNext.split(' ').length) < 20
      ) {
        lines[i] = `${trimmedLine} ${trimmedNext}`; // merge
        lines.splice(i + 1, 1);
        i--; // re-evaluate merged line with following line
        continue;
      }

      // 2. Merge accidental paragraph split (e.g., word broken across newline) where next line starts lowercase and previous line not ending sentence
      if (
        !isBlockStarter(trimmedLine) &&
        !isBlockStarter(trimmedNext) &&
        trimmedLine.length < 140 &&
        !/[.!?]$/.test(trimmedLine) &&
        /^[a-z]/.test(trimmedNext) &&
        // avoid merging if previous clearly ended a list item bullet structure
        !/[:;-]$/.test(trimmedLine)
      ) {
        lines[i] = `${trimmedLine} ${trimmedNext}`;
        lines.splice(i + 1, 1);
        i--;
        continue;
      }
    }

    return lines.join('\n');
  };

  const normalizedContent = repairStreamingMarkdown(content);
  // First, parse markdown links and replace them with source numbers
  const parseAndReplaceLinks = (text: string): string => {
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let parsedText = text;
    
    let match;
    while ((match = markdownLinkRegex.exec(text)) !== null) {
      const [fullMatch, , url] = match;
      
      // Find the corresponding source number
      const source = sources.find(s => s.url === url.trim());
      if (source) {
        parsedText = parsedText.replace(fullMatch, `[${source.number}]`);
      }
    }
    
    return parsedText;
  };

  // Custom markdown components that handle source numbers inline
  const customComponents = {
    ...markdownComponents,
    // Override text node processing to handle source references
    p: ({ children, ...props }: any) => {
      return <p className="my-3" {...props}>{processChildren(children)}</p>;
    },
    li: ({ children, ...props }: any) => {
      return <li {...props}>{processChildren(children)}</li>;
    },
    td: ({ children, ...props }: any) => {
      return <td {...props}>{processChildren(children)}</td>;
    },
    th: ({ children, ...props }: any) => {
      return <th {...props}>{processChildren(children)}</th>;
    },
    strong: ({ children, ...props }: any) => {
      return <strong {...props}>{processChildren(children)}</strong>;
    },
    em: ({ children, ...props }: any) => {
      return <em {...props}>{processChildren(children)}</em>;
    },
    h1: ({ children, ...props }: any) => {
      return <h1 className="text-2xl font-bold mt-6 mb-3 first:mt-0" {...props}>{processChildren(children)}</h1>;
    },
    h2: ({ children, ...props }: any) => {
      return <h2 className="text-xl font-bold mt-5 mb-3 first:mt-0" {...props}>{processChildren(children)}</h2>;
    },
    h3: ({ children, ...props }: any) => {
      return <h3 className="text-lg font-semibold mt-4 mb-2 first:mt-0" {...props}>{processChildren(children)}</h3>;
    },
    h4: ({ children, ...props }: any) => {
      return <h4 className="text-base font-semibold mt-3 mb-2 first:mt-0" {...props}>{processChildren(children)}</h4>;
    },
    h5: ({ children, ...props }: any) => {
      return <h5 className="text-sm font-semibold mt-3 mb-2 first:mt-0" {...props}>{processChildren(children)}</h5>;
    },
    h6: ({ children, ...props }: any) => {
      return <h6 className="text-sm font-medium mt-3 mb-2 first:mt-0" {...props}>{processChildren(children)}</h6>;
    },
  };

  // Function to process children and replace source references
  const processChildren = (children: any): any => {
    if (typeof children === 'string') {
      return processTextWithSourceReferences(children);
    }
    
    if (Array.isArray(children)) {
      return children.map((child, index) => {
        if (typeof child === 'string') {
          return processTextWithSourceReferences(child);
        }
        return child;
      });
    }
    
    return children;
  };

  // Function to process text and replace source references with clickable buttons
  const processTextWithSourceReferences = (text: string) => {
    if (typeof text !== 'string') return text;
    
    const sourceRefRegex = /\[(\d+)\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = sourceRefRegex.exec(text)) !== null) {
      const [fullMatch, numberStr] = match;
      const number = parseInt(numberStr, 10);
      const source = sources.find(s => s.number === number);
      
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      
      // Add clickable source reference
      if (source) {
        parts.push(
          <button
            key={`source-${number}-${match.index}`}
            onClick={() => onSourceClick?.(source)}
            className={cn(
              "inline-flex items-center justify-center w-5 h-5 text-xs font-medium rounded-full",
              "bg-emerald-500 text-white hover:bg-emerald-400 transition-colors",
              "mx-0.5 cursor-pointer align-baseline"
            )}
            title={`Source: ${source.title}`}
          >
            {number}
          </button>
        );
      } else {
        // Fallback if source not found
        parts.push(fullMatch);
      }
      
      lastIndex = match.index + fullMatch.length;
    }
    
    // Add remaining text after the last match
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };

  // Parse content and replace markdown links with source numbers (after normalization)
  const textWithSourceNumbers = parseAndReplaceLinks(normalizedContent);

  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={customComponents}
      >
        {textWithSourceNumbers}
      </ReactMarkdown>
    </div>
  );
};
